import type { Department } from '@opti-sea/core';
import {
  compareRisqCodes,
  isDone,
  prepSummary,
  progressByPhase,
  progressByPosition,
  resolveFlags,
  resolvePositions,
  summarizeFinalChecks,
  summarizeStatuses,
  type FinalChecksSummary,
  type InspectionPlan,
  type PrepSummary,
  type ProgressCount,
  type QuestionFlags,
  type ResolvedFlags,
  type ResolvedPositions,
  type RisqPrepTiming,
  type RisqStatus,
  type RisqWorkType,
  type StatusSummary,
} from '@opti-sea/risq';

/**
 * Pure RISQ views for the dashboard, built from rows read through the Data API. Same layer rules
 * as the phone and the database (`packages/risq`, PLAN §2.11).
 */

export interface Section {
  id: string;
  code: string;
  sortOrder: number;
}

export interface Question {
  id: string;
  key: string;
  code: string;
  sectionId: string;
  sectionCode: string;
  textEn: string;
  guidanceEn: string | null;
  adviceEn: string | null;
  workTypes: RisqWorkType[];
  prepTiming: RisqPrepTiming[];
  /** Default distribution from the master file, in position order. */
  basePositions: string[];
}

export interface Content {
  versionId: string;
  versionCode: string;
  sections: Section[];
  /** In natural code order. */
  questions: Question[];
}

export interface Layers {
  /** Company layer: question key → positions. */
  companyPositions: Map<string, string[]>;
  /** Vessel layer: vessel id → question key → positions. */
  vesselPositions: Map<string, Map<string, string[]>>;
  /** Company flags: question key → flags. */
  flags: Map<string, QuestionFlags>;
}

export interface ItemRow {
  id: string;
  questionId: string;
  status: RisqStatus;
  statusChangedAt: string | null;
  statusChangedBy: string | null;
  statusChangedPosition: string | null;
  lastNote: string | null;
  attentionFlag: boolean;
  needsRecheck: boolean;
  archivedAt: string | null;
}

export interface VesselItem {
  id: string;
  vesselId: string;
  question: Question;
  status: RisqStatus;
  statusChangedAt: string | null;
  statusChangedBy: string | null;
  lastNote: string | null;
  attentionFlag: boolean;
  needsRecheck: boolean;
  positions: ResolvedPositions;
  flags: ResolvedFlags;
}

export function companyPositionsOf(question: Question, layers: Layers): ResolvedPositions {
  return resolvePositions({
    base: question.basePositions,
    company: layers.companyPositions.get(question.key) ?? null,
  });
}

export function vesselPositionsOf(
  question: Question,
  layers: Layers,
  vesselId: string,
): ResolvedPositions {
  return resolvePositions({
    base: question.basePositions,
    company: layers.companyPositions.get(question.key) ?? null,
    vessel: layers.vesselPositions.get(vesselId)?.get(question.key) ?? null,
  });
}

export function flagsOf(question: Question, layers: Layers): ResolvedFlags {
  return resolveFlags(
    { workTypes: question.workTypes, prepTiming: question.prepTiming },
    layers.flags.get(question.key) ?? null,
  );
}

/** Items of one vessel with the layers applied; archived items left out; natural code order. */
export function vesselItems(
  content: Content,
  layers: Layers,
  vesselId: string,
  rows: readonly ItemRow[],
): VesselItem[] {
  const byId = new Map(content.questions.map((q) => [q.id, q]));
  const items: VesselItem[] = [];
  for (const row of rows) {
    const question = byId.get(row.questionId);
    if (question === undefined || row.archivedAt !== null) {
      continue;
    }
    items.push({
      id: row.id,
      vesselId,
      question,
      status: row.status,
      statusChangedAt: row.statusChangedAt,
      statusChangedBy: row.statusChangedBy,
      lastNote: row.lastNote,
      attentionFlag: row.attentionFlag,
      needsRecheck: row.needsRecheck,
      positions: vesselPositionsOf(question, layers, vesselId),
      flags: flagsOf(question, layers),
    });
  }
  return items.sort((a, b) => compareRisqCodes(a.question.code, b.question.code));
}

export interface VesselOverview {
  summary: StatusSummary;
  byPosition: { positions: { code: string; progress: ProgressCount }[]; unassigned: ProgressCount };
  byPhase: { timing: RisqPrepTiming; progress: ProgressCount }[];
  prep: PrepSummary | null;
}

/** Fleet / vessel numbers: status counts, progress per position and per preparation flag. */
export function vesselOverview(
  items: readonly VesselItem[],
  positionOrder: readonly string[],
  inspection: InspectionPlan | null,
  today: string,
): VesselOverview {
  return {
    summary: summarizeStatuses(items.map((i) => i.status)),
    byPosition: progressByPosition(
      items.map((i) => ({ status: i.status, positions: i.positions.codes })),
      positionOrder,
    ),
    byPhase: progressByPhase(
      items.map((i) => ({ status: i.status, prepTiming: i.flags.prepTiming })),
    ),
    prep: inspection === null ? null : prepSummary(today, inspection),
  };
}

/** Positions with questions on the vessel but nobody assigned (fleet page warning). */
export function positionsWithoutPerson(
  items: readonly VesselItem[],
  assignedPositions: readonly string[],
): string[] {
  const needed = new Set(items.flatMap((i) => i.positions.codes));
  return [...needed].filter((code) => !assignedPositions.includes(code));
}

/**
 * Questions a position works on (same rule as the database for questions of the week): the
 * Master — everything; the C/E — own and every engine question; others — own questions.
 */
export function itemsOfPosition(
  items: readonly VesselItem[],
  position: string,
  departmentOf: (code: string) => Department | undefined,
): VesselItem[] {
  return items.filter(
    (i) =>
      position === 'MASTER' ||
      i.positions.codes.includes(position) ||
      (position === 'CE' && i.positions.codes.some((c) => departmentOf(c) === 'engine')),
  );
}

/** Unfinished questions of last week's set that are still in the position's list. */
export function unfinishedFromLastWeek(
  lastWeekItemIds: readonly string[],
  candidates: readonly VesselItem[],
): string[] {
  const open = new Set(candidates.filter((i) => !isDone(i.status)).map((i) => i.id));
  return lastWeekItemIds.filter((id) => open.has(id));
}

/** Monday (ISO week, UTC date — PLAN §2.4) of the week `weeks` after the week of `now`. */
export function weekStart(now: Date, weeks = 0): string {
  const day = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  day.setUTCDate(day.getUTCDate() - ((day.getUTCDay() + 6) % 7) + weeks * 7);
  return day.toISOString().slice(0, 10);
}

export interface ReassignChange {
  key: string;
  code: string;
  from: readonly string[];
  to: readonly string[];
}

/**
 * Preview of a mass reassignment (D14): for each chosen question, the positions before and after
 * at the target layer (company, or one vessel). `positions` null = return to the layer above.
 * Questions that would not change are counted in `unchanged`, like the server does.
 */
export function reassignPreview(
  questions: readonly Question[],
  layers: Layers,
  vesselId: string | null,
  positions: readonly string[] | null,
  positionOrder: readonly string[],
): { changes: ReassignChange[]; unchanged: number } {
  const ordered = (codes: readonly string[]) =>
    [...codes].sort((a, b) => positionOrder.indexOf(a) - positionOrder.indexOf(b));
  const changes: ReassignChange[] = [];
  let unchanged = 0;
  for (const q of questions) {
    const now =
      vesselId === null ? companyPositionsOf(q, layers) : vesselPositionsOf(q, layers, vesselId);
    const hasOwnRow =
      vesselId === null
        ? layers.companyPositions.has(q.key)
        : (layers.vesselPositions.get(vesselId)?.has(q.key) ?? false);
    let to: readonly string[];
    if (positions === null) {
      if (!hasOwnRow) {
        unchanged += 1;
        continue;
      }
      to =
        vesselId === null
          ? q.basePositions
          : resolvePositions({
              base: q.basePositions,
              company: layers.companyPositions.get(q.key) ?? null,
            }).codes;
    } else {
      to = ordered(positions);
      if (to.join(',') === ordered(now.codes).join(',')) {
        unchanged += 1;
        continue;
      }
    }
    changes.push({ key: q.key, code: q.code, from: now.codes, to });
  }
  return { changes, unchanged };
}

/** "From → to" groups of a preview with how many questions each, biggest first. */
export function groupChanges(
  changes: readonly ReassignChange[],
): { from: readonly string[]; to: readonly string[]; count: number }[] {
  const groups = new Map<
    string,
    { from: readonly string[]; to: readonly string[]; count: number }
  >();
  for (const c of changes) {
    const id = `${c.from.join(',')}>${c.to.join(',')}`;
    const group = groups.get(id) ?? { from: c.from, to: c.to, count: 0 };
    group.count += 1;
    groups.set(id, group);
  }
  return [...groups.values()].sort((a, b) => b.count - a.count);
}

// ---------------------------------------------------------------------------------------------
// Final checks (Task 6b)
// ---------------------------------------------------------------------------------------------

export interface FinalCheckGroupRow {
  id: string;
  titleEn: string;
  timing: string;
  positionCodes: string[];
  sortOrder: number;
}

export interface FinalCheckItemRow {
  id: string;
  groupId: string;
  sortOrder: number;
  textEn: string;
  inputKind: string | null;
  inputLabelEn: string | null;
}

export interface FinalCheckMarkRow {
  itemId: string;
  checked: boolean;
  value: string | null;
  changedAt: string;
  changedPosition: string | null;
}

export interface FinalChecksOverview {
  summary: FinalChecksSummary;
  groups: { id: string; titleEn: string; positionCodes: string[]; summary: FinalChecksSummary }[];
  /** Items with an input field, in list order, with what the crew entered (null = not yet). */
  values: {
    itemId: string;
    labelEn: string;
    kind: string;
    value: string | null;
    changedPosition: string | null;
  }[];
}

/** Progress of a vessel's Final checks for one planned inspection date (marks of that date). */
export function finalChecksOverview(
  groups: readonly FinalCheckGroupRow[],
  items: readonly FinalCheckItemRow[],
  marks: readonly FinalCheckMarkRow[],
): FinalChecksOverview {
  const markOf = new Map(marks.map((m) => [m.itemId, m]));
  const orderedGroups = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
  const groupRank = new Map(orderedGroups.map((g, index) => [g.id, index]));
  const orderedItems = items
    .filter((i) => groupRank.has(i.groupId))
    .sort(
      (a, b) =>
        (groupRank.get(a.groupId) ?? 0) - (groupRank.get(b.groupId) ?? 0) ||
        a.sortOrder - b.sortOrder,
    );
  return {
    summary: summarizeFinalChecks(orderedItems.map((i) => markOf.get(i.id))),
    groups: orderedGroups.map((g) => ({
      id: g.id,
      titleEn: g.titleEn,
      positionCodes: g.positionCodes,
      summary: summarizeFinalChecks(
        orderedItems.filter((i) => i.groupId === g.id).map((i) => markOf.get(i.id)),
      ),
    })),
    values: orderedItems
      .filter((i) => i.inputKind !== null)
      .map((i) => {
        const mark = markOf.get(i.id);
        return {
          itemId: i.id,
          labelEn: i.inputLabelEn ?? i.textEn,
          kind: i.inputKind ?? 'text',
          value: mark?.checked === true ? mark.value : null,
          changedPosition: mark?.checked === true ? mark.changedPosition : null,
        };
      }),
  };
}
